import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDocumentCategoryInput,
  DocumentCategory,
  ProjectDocument,
  UpdateDocumentCategoryInput,
} from '@faktorplus/shared';
import { apiRequest, uploadMultipart } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

const DICTIONARY_STALE_TIME = 5 * 60 * 1000;

/* ───────────────────────────── Categories ──────────────────────────────── */

/** `includeInactive` is only for the settings tab — pickers must not offer hidden rows. */
export function useDocumentCategories(includeInactive = false) {
  return useQuery({
    queryKey: queryKeys.documents.categories(includeInactive),
    queryFn: () =>
      apiRequest<DocumentCategory[]>('/document-categories', { query: { includeInactive } }),
    staleTime: DICTIONARY_STALE_TIME,
  });
}

function useInvalidateDocuments() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
}

export function useCreateDocumentCategory() {
  const invalidate = useInvalidateDocuments();

  return useMutation({
    mutationFn: (input: CreateDocumentCategoryInput) =>
      apiRequest<DocumentCategory>('/document-categories', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateDocumentCategory() {
  const invalidate = useInvalidateDocuments();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDocumentCategoryInput }) =>
      apiRequest<DocumentCategory>(`/document-categories/${id}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

/* ───────────────────────────── Documents ───────────────────────────────── */

export function useProjectDocuments(
  projectId: string,
  filters: { categoryId?: string; vendorId?: string },
) {
  return useQuery({
    queryKey: queryKeys.documents.forProject(projectId, filters),
    queryFn: () =>
      apiRequest<ProjectDocument[]>(`/projects/${projectId}/documents`, { query: filters }),
    placeholderData: (previous) => previous,
  });
}

/** Company templates — the same list for every role. */
export function useCompanyDocuments() {
  return useQuery({
    queryKey: queryKeys.documents.templates,
    queryFn: () => apiRequest<ProjectDocument[]>('/company-documents'),
    staleTime: DICTIONARY_STALE_TIME,
  });
}

export interface UploadDocumentInput {
  /** Null = a company template. */
  projectId: string | null;
  name: string;
  categoryId: string;
  vendorId?: string;
  file: File;
}

export function useUploadDocument() {
  const invalidate = useInvalidateDocuments();

  return useMutation({
    mutationFn: (input: UploadDocumentInput) =>
      uploadMultipart(
        input.projectId ? `/projects/${input.projectId}/documents` : '/company-documents',
        { name: input.name, categoryId: input.categoryId, vendorId: input.vendorId ?? '' },
        input.file,
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteDocument() {
  const invalidate = useInvalidateDocuments();

  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/documents/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
